import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Globe2 } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { notify } from '@/shared/services';
import { useTranslation } from '@/core/i18n';
import { LoginHero } from './login/LoginHero';
import { LoginForm } from './login/LoginForm';
import { LoginWristbandAlt } from './login/LoginWristbandAlt';
import { LoginFooter } from './login/LoginFooter';

// DEFERRED — Phase-1 redesign ships the wristband UI behind a hard-coded flag.
// There is currently no tenant-level config nor env var for this (no FEATURE_*,
// no TenantContext field). Felipe will decide later whether to wire it to a
// backend flag or a VITE_ env var. Flip to true locally to preview the block.
const NFC_LOGIN_ENABLED = false;

interface LocationState {
  from?: { pathname?: string };
}

/**
 * LoginPage — split-screen entry to Deck.
 *
 * Left column (animated coastal hero) communicates product identity and
 * showcases the three product domains; right column hosts the locale chip,
 * the credentials form, the optional wristband alt sign-in, and the
 * brand/region footer. Mirrors `shaka-deck-mockup-login.html` V1 (desktop
 * hero split).
 */
const LoginPage = () => {
  const { t } = useTranslation();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Resolve where to send the user after a successful auth.
   *
   * Priority:
   *   1. `?return=<encoded-url>` query param — set by SessionExpiredPage
   *      and any other public surface that wants to round-trip the user
   *      back. Hard-redirected flows (window.location.href) lose React
   *      Router state, so they use the query string instead.
   *   2. `location.state.from.pathname` — set by `<ProtectedRoute>` when
   *      it intercepts an unauthenticated navigation. React Router state
   *      survives client-side redirects.
   *   3. `/select` — the gateway/context selector, default landing.
   */
  const resolveRedirectTarget = (): string => {
    // Paths that should never be returned to after login — auth surfaces,
    // error pages, and other dead-end routes. Checked as prefixes so that
    // `/session-expired?return=…` and `/logout` both get caught.
    const NON_RETURNABLE = [
      '/login',
      '/logout',
      '/session-expired',
      '/access-denied',
      '/unsubscribe',
    ];

    const isSafe = (path: string): boolean =>
      path.startsWith('/') && !path.startsWith('//') && !NON_RETURNABLE.some((p) => path.startsWith(p));

    const returnParam = searchParams.get('return');
    if (returnParam) {
      try {
        const decoded = decodeURIComponent(returnParam);
        if (isSafe(decoded)) {
          return decoded;
        }
      } catch {
        // Fall through to other strategies.
      }
    }
    const from = (location.state as LocationState | null)?.from?.pathname;
    if (from && isSafe(from)) {
      return from;
    }
    return '/select';
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(resolveRedirectTarget(), { replace: true });
    }
    // resolveRedirectTarget reads searchParams + location internally —
    // re-running the effect when those change is desired.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, location, searchParams]);

  const redirectAfterLogin = () => {
    navigate(resolveRedirectTarget(), { replace: true });
  };

  const handleSubmit = async ({ email, password }: { email: string; password: string; rememberMe: boolean }) => {
    if (!email || !password) {
      notify.error(t('auth.enterBothFields'));
      return;
    }

    try {
      setIsSubmitting(true);
      await login({ email, password });
      notify.success(t('auth.welcomeBack'));
      redirectAfterLogin();
    } catch (error) {
      // shaka-api emits a typed 403 when the user's tenant is
      // suspended (Company.is_active=False). Route to the dedicated
      // editorial page instead of flashing a toast on the login form
      // — the operator can't recover from a toast.
      if (
        error instanceof Error
        && (error as Error & { code?: string }).code === 'tenant_suspended'
      ) {
        navigate('/account-suspended', { replace: true });
        return;
      }
      notify.error(error instanceof Error ? error.message : t('auth.invalidCredentials'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWristbandTap = () => {
    // Stub — full NFC flow is being rewired in a later phase.
    notify.info(t('auth.login.wristband.comingSoon'));
  };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[1.15fr_1fr]">
      <LoginHero />
      <section className="relative flex min-h-screen flex-col bg-gradient-to-b from-card to-sand-50 px-6 py-8 sm:px-10 lg:px-16">
        {/* form-panel-top (mockup): locale chip on the left, room for future
            slots on the right (sign up, etc.) when those flows exist. */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 font-mono text-[11px] text-muted-foreground transition-colors hover:bg-sand-100"
          >
            <Globe2 className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{t('auth.login.localeChip')}</span>
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center py-12">
          <div className="w-full max-w-sm opacity-0 animate-reveal">
            <LoginForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
            {NFC_LOGIN_ENABLED && (
              <LoginWristbandAlt onTap={handleWristbandTap} disabled={isSubmitting} />
            )}
            <LoginFooter />
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
